import { Check, Copy } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import {
  parseMessageContent,
  type InlineContent,
  type MessageText,
  type MessageEntity,
  type ContentBlock,
} from '@/lib/messageMarkup';
import { tokenizeMessageCode, MESSAGE_CODE_CLASSES as tokenClass } from '@/lib/messageCode';
import type { MessageActions } from '@/lib/conversation';
import { EntityText } from './EntityText';
import { Button } from './Button';

export function MessageCodeBlock({ text, language = '' }: { text: string; language?: string }) {
  const id = useId();
  const [copied, setCopied] = useState<{ value: string; state: 'done' | 'error' } | null>(null);
  const copy = copied?.value === text ? copied.state : 'idle';
  const tokens = useMemo(() => tokenizeMessageCode(text, language), [text, language]);
  async function copyCode() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ value: text, state: 'done' });
    } catch {
      setCopied({ value: text, state: 'error' });
    }
  }
  return (
    <figure
      data-slot="message-code"
      className="my-3 min-w-0 overflow-hidden rounded-sm border border-line bg-muted"
    >
      <figcaption className="flex items-center justify-between gap-3 border-b border-line px-3 py-2">
        <span id={id} className="font-mono text-xs text-fg-muted">
          {language || 'Код'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => void copyCode()} aria-label="Скопировать код">
          {copy === 'done' ? (
            <Check className="size-3.5" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
          {copy === 'done' ? 'Скопировано' : 'Копировать'}
        </Button>
      </figcaption>
      <pre
        tabIndex={0}
        aria-labelledby={id}
        className="max-h-96 overflow-auto p-4 font-mono text-xs leading-5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <code>
          {tokens.map((token, i) => (
            <span key={i} className={tokenClass[token.kind]}>
              {token.text}
            </span>
          ))}
        </code>
      </pre>
      <span role="status" className={copy === 'error' ? 'block px-3 pb-3 text-xs text-danger-fg' : 'sr-only'}>
        {copy === 'done'
          ? 'Код скопирован'
          : copy === 'error'
            ? 'Не удалось скопировать. Выдели код и нажми Ctrl+C.'
            : ''}
      </span>
    </figure>
  );
}

function Inline({
  nodes,
  onEntityOpen,
  people,
}: {
  people?: MessageActions['people'];
  nodes: InlineContent[];
  onEntityOpen?: MessageActions['onEntityOpen'];
}) {
  return nodes.map((node, i) => {
    switch (node.kind) {
      case 'text':
        return <span key={i}>{node.text}</span>;
      case 'code':
        return (
          <code
            key={i}
            className="rounded-xs border border-line bg-muted px-1 py-0.5 font-mono text-xs text-fg"
          >
            {node.text}
          </code>
        );
      case 'strong':
        return (
          <strong key={i}>
            <Inline nodes={node.children} people={people} onEntityOpen={onEntityOpen} />
          </strong>
        );
      case 'emphasis':
        return (
          <em key={i}>
            <Inline nodes={node.children} people={people} onEntityOpen={onEntityOpen} />
          </em>
        );
      case 'underline':
        return (
          <u key={i}>
            <Inline nodes={node.children} people={people} onEntityOpen={onEntityOpen} />
          </u>
        );
      case 'strike':
        return (
          <s key={i}>
            <Inline nodes={node.children} people={people} onEntityOpen={onEntityOpen} />
          </s>
        );
      case 'link':
        return (
          <a
            key={i}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xs text-brand-fg underline decoration-brand-fg/40 underline-offset-4 hover:decoration-brand-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <Inline nodes={node.children} />
          </a>
        );
      case 'entity':
        return onEntityOpen ? (
          <EntityText
            key={i}
            prefix={node.entity.kind === 'channel' ? '#' : ''}
            name={node.entity.name}
            person={
              node.entity.kind === 'user' ? people?.find((person) => person.id === node.entity.id) : undefined
            }
            onClick={(event) => onEntityOpen(node.entity, event.currentTarget)}
          />
        ) : (
          <span key={i} className="font-medium">
            {node.entity.kind === 'channel' ? '#' : ''}
            {node.entity.name}
          </span>
        );
    }
  });
}

function previewText(nodes: InlineContent[]): string {
  return nodes
    .map((node) =>
      'children' in node
        ? previewText(node.children)
        : node.kind === 'entity'
          ? (node.entity.kind === 'channel' ? '#' : '') + node.entity.name
          : node.text,
    )
    .join('');
}

function blockText(block: ContentBlock): string {
  return block.kind === 'code'
    ? block.text
    : block.kind === 'list'
      ? block.items.map((items) => items.map(blockText).join('\n')).join('\n')
      : block.kind === 'quote' && block.blocks
        ? block.blocks.map(blockText).join('\n')
        : previewText(block.children);
}
function Blocks({
  blocks,
  people,
  onEntityOpen,
}: {
  blocks: ContentBlock[];
  people?: MessageActions['people'];
  onEntityOpen?: MessageActions['onEntityOpen'];
}) {
  return blocks.map((block, i) => {
    if (block.kind === 'code')
      return <MessageCodeBlock key={i} text={block.text} language={block.language} />;
    if (block.kind === 'list') {
      const children = block.items.map((items, index) => (
        <li key={index} className="pl-1">
          <Blocks blocks={items} people={people} onEntityOpen={onEntityOpen} />
        </li>
      ));
      return block.ordered ? (
        <ol key={i} start={block.start} className="list-decimal space-y-1 pl-5">
          {children}
        </ol>
      ) : (
        <ul key={i} className="list-disc space-y-1 pl-5">
          {children}
        </ul>
      );
    }
    if (block.kind === 'quote')
      return (
        <blockquote key={i} className="border-l-2 border-line-strong pl-3 text-fg-muted">
          {block.blocks ? (
            <div className="space-y-2">
              <Blocks blocks={block.blocks} people={people} onEntityOpen={onEntityOpen} />
            </div>
          ) : (
            <Inline nodes={block.children} people={people} onEntityOpen={onEntityOpen} />
          )}
        </blockquote>
      );
    return (
      <p key={i}>
        <Inline nodes={block.children} people={people} onEntityOpen={onEntityOpen} />
      </p>
    );
  });
}
/** Presentation only: comments and conversations share this renderer and entity resolver. */
export function MessageContent({
  content,
  entities = [],
  people,
  onEntityOpen,
  preview = false,
}: {
  content: MessageText;
  entities?: readonly MessageEntity[];
  people?: MessageActions['people'];
  /** Two-line, text-only inbox excerpt: no clipped links, buttons or code tools in the tab order. */
  preview?: boolean;
  onEntityOpen?: MessageActions['onEntityOpen'];
}) {
  const blocks = useMemo(() => parseMessageContent(content, entities), [content, entities]);
  if (preview)
    return (
      <p
        data-slot="message-preview"
        className="line-clamp-2 text-sm leading-5 break-words whitespace-pre-wrap text-fg"
      >
        {blocks.map(blockText).join('\n')}
      </p>
    );
  return (
    <div
      data-slot="message-content"
      data-format={content.format}
      className="min-w-0 space-y-2 text-sm leading-5 break-words whitespace-pre-wrap text-fg"
    >
      <Blocks blocks={blocks} people={people} onEntityOpen={onEntityOpen} />
    </div>
  );
}
